import { runInAction, toJS } from "mobx";
import isDefined from "../Core/isDefined";
import TerriaError from "../Core/TerriaError";
import CommonStrata from "../Models/Definition/CommonStrata";
import CatalogFunctionJobMixin from "./CatalogFunctionJobMixin";
import CatalogMemberMixin from "./CatalogMemberMixin";
function CatalogFunctionMixin(Base) {
  class CatalogFunctionMixin extends CatalogMemberMixin(Base) {
    /**
     * Function parameters are rendered as ParameterEditors, their values directly map to the `parameters` trait. When a FunctionParameter value is modified, it will automatically update `parameters` trait.
     *
     * When a job is created, the `parameters` are copied across automatically (see {@link CatalogFunctionMixin#submitJob})
     */

    /**
     * Create new job.
     * Note: `name` and `parameters` traits are automatically copied across when submitted ({@link CatalogFunctionMixin#submitJob})
     * All user-configured job parameters should be in the `parameters` trait, this is the ensure that function parameter state behaves correctly, and that values can be easily copied across jobs.
     *
     * Other job traits can be set in this function, as long as they aren't related to function parameters - for example the `url` and `processIdentier` trait for WPS are copied from the WPSCatalogFunction.
     */

    /**
     * Submit job:
     * - create new job {@link CatalogFunctionMixin#createJob}
     * - sets job traits (`name`, `parameters`, ...)
     * - invokes job {@link CatalogFunctionJobMixin#invoke}
     * - adds to workbench/models (in user added data) if successfully submitted
     * @returns new job
     */
    async submitJob() {
      try {
        const timestamp = new Date().toISOString();
        const newJob = await this.createJob("".concat(this.uniqueId, "-").concat(timestamp));
        if (!CatalogFunctionJobMixin.isMixedInto(newJob)) {
          throw "Error creating job catalog item - ".concat(newJob.type, " is not a valid jobType");
        }

        // Give default name if needed
        if (!isDefined(runInAction(() => newJob.name))) {
          newJob.setTrait(CommonStrata.user, "name", "".concat(newJob.typeName, " ").concat(timestamp));
        }
        newJob.setTrait(CommonStrata.user, "parameters", toJS(this.parameters));
        (await newJob.loadMetadata()).throwIfError();
        this.terria.addModel(newJob);
        this.terria.catalog.userAddedDataGroup.add(CommonStrata.user, newJob);
        this.terria.workbench.add(newJob).then(r => r.raiseError(this.terria));
        await newJob.invoke();
        return newJob;
      } catch (error) {
        throw TerriaError.from(error, {
          title: "Error submitting `".concat(this.typeName, "` job")
        });
      }
    }
    get hasCatalogFunctionMixin() {
      return true;
    }
  }
  return CatalogFunctionMixin;
}
(function (_CatalogFunctionMixin) {
  function isMixedInto(model) {
    return model && model.hasCatalogFunctionMixin;
  }
  _CatalogFunctionMixin.isMixedInto = isMixedInto;
})(CatalogFunctionMixin || (CatalogFunctionMixin = {}));
export default CatalogFunctionMixin;